import { useState } from 'react';

import { nanoid } from '@reduxjs/toolkit';

import { useAppDispatch } from 'state/hooks';
import {
  addDraftSpecialDate,
  useSpecialEventDateCreateMutation,
} from 'state/userAdminSlices';
import { checkUserStatus } from 'state/userSlice';

import { SpecialDateRequest } from 'appTypes';
import { SOMETHING_WENT_WRONG_SAVING_MSG } from 'appUtils';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { StlButton } from 'features/common/StlButton/StlButton';
import { useSubscription } from 'features/common/hooks/useSubscription';
import { showToast } from 'features/common/toasts/ToastProvider';

import { useUprStatus } from '../hooks';
import SpecialDateSetting from './SpecialDateSettings';
import { useDatesQuota } from './hooks';
import { isProjectActive } from './utils';

interface IAddSpecialEventBtn {
  subscriptionId?: string;
  projectId?: string;
  text?: string;
  inline?: boolean;
}
const INLINE_WIDTH = 90;
const WIDTH = 145;

export default function AddSpecialEventBtn({
  subscriptionId,
  projectId,
  text,
  inline = false,
}: IAddSpecialEventBtn) {
  const dispatch = useAppDispatch();
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const { freeQuota } = useDatesQuota(subscriptionId, projectId);
  const [createSpecialEventDate] = useSpecialEventDateCreateMutation();
  const { isEditable } = useUprStatus();
  const { subscription, project_data } = useSubscription(
    subscriptionId,
    projectId,
  );
  const isSubAdmin = subscription?.user_is_sub_admin;
  if (!isSubAdmin) return null;

  const isProject = project_data !== null;

  const handleClick = () => {
    if (freeQuota <= 0) return;
    setSettingsOpen(true);
  };

  const savePermanent = (req: SpecialDateRequest) => {
    createSpecialEventDate({
      project_slug: project_data?.project_id,
      special_date_request: req,
    })
      .unwrap()
      .then(() => showToast('success', 'Special Event requested successfully'))
      .catch((rejected) => {
        showToast('error', SOMETHING_WENT_WRONG_SAVING_MSG);
        console.error(rejected);
      });
    dispatch(checkUserStatus());
  };

  const save = (subscription_id: string, req: SpecialDateRequest) => {
    if (isProject) savePermanent(req);
    else saveTemp(subscription_id, req);
  };

  const saveTemp = (subscription_id: string, req: SpecialDateRequest) => {
    dispatch(
      addDraftSpecialDate({
        subscription_id,
        date_request: {
          ...req,
          tempId: nanoid(),
        },
      }),
    );
  };

  const projectActive = isProjectActive(project_data);
  const canAdd = freeQuota > 0 && (isEditable || (isProject && projectActive));

  return (
    <>
      <StlButton
        variant="text"
        icon={<FontAwesomeIcon name="plus" size="0.875rem" gold />}
        width={inline ? INLINE_WIDTH : WIDTH}
        value={text ?? 'Add Special Event'}
        handleClick={handleClick}
        sx={{ margin: inline ? 0 : '0 auto !important' }}
        isActive={canAdd}
      />
      <SpecialDateSetting
        subscriptionId={subscriptionId}
        projectId={projectId}
        onSave={save}
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
}
